import React, { useState, useEffect } from 'react';
import { 
  X, 
  Save, 
  AlertTriangle,
  Loader2,
  Folder,
  FolderOpen,
  Image
} from 'lucide-react';

const AddEditCategoryModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  category = null, 
  categories = [],
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    parentId: '',
    iconUrl: '',
    isActive: true
  });

  const [errors, setErrors] = useState({});
  const isEditMode = !!category;

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        parentId: category.parentId || '',
        iconUrl: category.iconUrl || '',
        isActive: category.isActive ?? true
      });
    } else {
      setFormData({
        name: '',
        parentId: '',
        iconUrl: '',
        isActive: true
      });
    }
    setErrors({});
  }, [category, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }

    // Prevent circular reference in edit mode
    if (isEditMode && formData.parentId === category.id) {
      newErrors.parentId = 'Category cannot be its own parent';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      name: formData.name.trim(),
      parentId: formData.parentId || null,
      iconUrl: formData.iconUrl || null
    };

    onSave(submitData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Filter out current category and its children from parent options (in edit mode)
  const getAvailableParentCategories = () => {
    if (!isEditMode) {
      return categories.filter(cat => cat.id !== category?.id);
    }

    // In edit mode, exclude current category and its descendants
    const excludeIds = new Set([category.id]);
    
    // Add all descendants to exclude list
    const addDescendants = (parentId) => {
      categories.forEach(cat => {
        if (cat.parentId === parentId && !excludeIds.has(cat.id)) {
          excludeIds.add(cat.id);
          addDescendants(cat.id);
        }
      });
    };
    
    addDescendants(category.id);
    
    return categories.filter(cat => !excludeIds.has(cat.id));
  };

  const availableParentCategories = getAvailableParentCategories();

  if (!isOpen) return null;

  return (
    <div translate="no" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? 'Edit Category' : 'Add New Category'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4 flex items-center">
              <Folder className="w-5 h-5 mr-2" />
              Category Information
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Puja Ceremonies"
                  disabled={loading}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Parent Category
                </label>
                <select
                  value={formData.parentId}
                  onChange={(e) => handleInputChange('parentId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.parentId ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading}
                >
                  <option value="">None (Root Category)</option>
                  {availableParentCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.parent ? `${cat.parent.name} → ${cat.name}` : cat.name}
                    </option>
                  ))}
                </select>
                {errors.parentId && (
                  <p className="mt-1 text-sm text-red-600">{errors.parentId}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Select a parent category to create a subcategory
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icon URL
                </label>
                <div className="relative">
                  <Image className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="url"
                    value={formData.iconUrl}
                    onChange={(e) => handleInputChange('iconUrl', e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com/icon.png"
                    disabled={loading}
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Optional: URL to an icon image for this category
                </p>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <span className="ml-2 text-sm text-gray-700">Active Category</span>
                </label>
                <p className="mt-1 text-sm text-gray-500">
                  Inactive categories will be hidden from users
                </p>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          {(formData.name || formData.iconUrl) && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 mb-3">Preview</h3>
              <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border">
                {formData.iconUrl ? (
                  <img 
                    src={formData.iconUrl} 
                    alt="Category icon" 
                    className="w-8 h-8 rounded object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <FolderOpen className="w-8 h-8 text-blue-500" />
                )}
                <div>
                  <p className="font-medium text-gray-900">
                    {formData.name || 'Category Name'}
                  </p>
                  {formData.parentId && (
                    <p className="text-sm text-gray-500">
                      Under: {categories.find(c => c.id === formData.parentId)?.name}
                    </p>
                  )}
                </div>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  formData.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {formData.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          )}

          {/* Warning for Edit Mode */}
          {isEditMode && category?.serviceCount > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-yellow-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Category has services
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    This category contains {category.serviceCount} service(s). 
                    Changes to this category will affect all associated services.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </span>
              ) : (
                <span className="flex items-center">
                  <Save className="w-4 h-4 mr-2" />
                  {isEditMode ? 'Update Category' : 'Create Category'}
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditCategoryModal;
